import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { dataAPI, vaultsAPI } from '../services/api';
import { Card, Input, PillButton, Chip } from '../ui';
import { FIELD_DEFS, CATEGORIES, categoryOf, rawTypeForNewItem } from '../utils/itemTypes';
import PasswordGenerator from '../components/PasswordGenerator';
import { ReactComponent as KeyRoundIcon } from '../assets/icons/key-round.svg';
import { ReactComponent as EyeIcon } from '../assets/icons/eye.svg';
import { ReactComponent as EyeOffIcon } from '../assets/icons/eye-off.svg';
import './ItemForm.css';

// Categorías que se pueden crear desde cero (excluye "all"/"favorites", que
// no son un tipo de dato, y los alias legado que solo existen en lo importado).
// "passkey" tampoco: nace en la extensión o la app al registrarte en una web,
// nunca a mano desde el panel (ver design/PASSKEYS.md).
const CREATABLE_CATEGORIES = CATEGORIES.filter((c) => !['all', 'favorites', 'passkey'].includes(c.id));

function ItemForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const rfidId = localStorage.getItem('rfidId');
  const isEditing = !!id;

  const [category, setCategory] = useState(searchParams.get('type') || '');
  const [originalData, setOriginalData] = useState({});
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [vault, setVault] = useState('personal');
  const [vaults, setVaults] = useState([]);
  const [values, setValues] = useState({});
  const [revealed, setRevealed] = useState({});
  const [generatorFor, setGeneratorFor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(isEditing);

  useEffect(() => {
    if (!rfidId) return;
    vaultsAPI.list(rfidId).then((res) => setVaults(res.data?.vaults || [])).catch(() => setVaults([]));
  }, [rfidId]);

  useEffect(() => {
    if (!isEditing) return;
    setLoading(true);
    dataAPI.get(id, rfidId).then((res) => {
      const raw = res.data;
      const data = typeof raw?.data === 'string' ? JSON.parse(raw.data) : (raw?.data || {});
      setOriginalData(data);
      setCategory(categoryOf(data.type));
      setTitle(raw.name || '');
      setNotes(data.notes || '');
      setTagsText((raw.tags || []).join(', '));
      setVault(data.vault || 'personal');
      setValues(data);
    }).catch(() => setError(t('dataEntry.saveError'))).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditing, rfidId]);

  const fields = useMemo(() => (FIELD_DEFS[category] || []).filter((f) => f.key !== 'notes'), [category]);

  const setValue = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));

  const save = useCallback(async () => {
    if (!title.trim() || !category) return;
    setSaving(true);
    setError('');
    try {
      const tags = tagsText.split(',').map((s) => s.trim()).filter(Boolean);
      const data = {
        ...originalData, // conserva lo que no se edita aquí (changeHistory, customFields, alerts...)
        ...values,
        type: rawTypeForNewItem(category),
        vault,
        notes,
      };
      const res = await dataAPI.save({
        rfidId,
        name: title.trim(),
        description: notes || title.trim(),
        data,
        tags,
        ...(isEditing ? { itemId: id } : {}),
      });
      navigate(`/vault/${res.data?.id || id}`);
    } catch (err) {
      setError(err.response?.data?.error || t('dataEntry.saveError'));
    } finally {
      setSaving(false);
    }
  }, [title, category, tagsText, originalData, values, vault, notes, rfidId, isEditing, id, navigate, t]);

  if (loading) return <p className="sgc-item-form__loading">{t('common.loading', 'Cargando…')}</p>;

  // Paso 1: elegir tipo (solo al crear, sin ?type= en la URL)
  if (!isEditing && !category) {
    return (
      <div className="sgc-item-form">
        <h1 className="sgc-item-form__title">{t('dataEntry.addWhat')}</h1>
        <div className="sgc-item-form__type-grid">
          {CREATABLE_CATEGORIES.map((c) => (
            <button key={c.id} type="button" className="sgc-item-form__type" onClick={() => setCategory(c.id)}>
              <c.icon width={22} height={22} aria-hidden="true" />
              <span>{t(c.labelKey)}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="sgc-item-form">
      <h1 className="sgc-item-form__title">{isEditing ? t('dataEntry.editTitle') : t('dataEntry.newEntry')}</h1>

      <Card className="sgc-item-form__fields">
        <Input label={t('dataEntry.title')} value={title} onChange={(e) => setTitle(e.target.value)} />

        {fields.map((f) => {
          if (f.type === 'textarea') {
            return (
              <div key={f.key} className="sgc-item-form__field">
                <label>{t(f.labelKey)}</label>
                <textarea
                  value={values[f.key] || ''}
                  onChange={(e) => setValue(f.key, e.target.value)}
                  rows={3}
                />
              </div>
            );
          }
          if (f.type === 'checkbox') {
            return (
              <label key={f.key} className="sgc-item-form__checkbox">
                <input type="checkbox" checked={!!values[f.key]} onChange={(e) => setValue(f.key, e.target.checked)} />
                {t(f.labelKey)}
              </label>
            );
          }
          if (f.type === 'file') {
            return (
              <div key={f.key} className="sgc-item-form__field">
                <label>{t(f.labelKey)}</label>
                <input
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setValue('fileName', file.name);
                    const reader = new FileReader();
                    reader.onload = () => setValue('fileData', reader.result);
                    reader.readAsDataURL(file);
                  }}
                />
                {values.fileName && <span className="sgc-item-form__file-name">{values.fileName}</span>}
              </div>
            );
          }
          const inputType = f.secret && !revealed[f.key] ? 'password' : (f.type === 'password' ? 'text' : f.type);
          return (
            <Input
              key={f.key}
              icon={f.generate ? KeyRoundIcon : undefined}
              label={t(f.labelKey)}
              type={inputType}
              value={values[f.key] || ''}
              onChange={(e) => setValue(f.key, e.target.value)}
              rightAction={f.secret && (
                <button type="button" onClick={() => setRevealed((p) => ({ ...p, [f.key]: !p[f.key] }))} title={t('dashboard.reveal')}>
                  {revealed[f.key] ? <EyeOffIcon width={16} height={16} aria-hidden="true" /> : <EyeIcon width={16} height={16} aria-hidden="true" />}
                </button>
              )}
            />
          );
        })}

        {fields.some((f) => f.generate) && (
          <PillButton variant="secondary" icon={KeyRoundIcon} onClick={() => setGeneratorFor('password')}>
            {t('dataEntry.generatePassword')}
          </PillButton>
        )}

        <div className="sgc-item-form__field">
          <label>{t('dataEntry.notes')}</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
        </div>

        <Input label={t('dataEntry.tagsLabel')} placeholder={t('dataEntry.tagsCommaSeparated')} value={tagsText} onChange={(e) => setTagsText(e.target.value)} />

        <div className="sgc-item-form__field">
          <label>{t('dataEntry.vaultLabel')}</label>
          <div className="sgc-item-form__vaults">
            <Chip label={t('dashboard.personalVault')} selected={vault === 'personal'} onClick={() => setVault('personal')} />
            {vaults.map((v) => (
              <Chip key={v.vaultId} label={v.name} selected={vault === v.vaultId} onClick={() => setVault(v.vaultId)} />
            ))}
          </div>
        </div>

        {error && <p className="sgc-input__error">{error}</p>}

        <div className="sgc-item-form__actions">
          <PillButton variant="secondary" onClick={() => navigate(-1)}>{t('dataEntry.cancel')}</PillButton>
          <PillButton disabled={!title.trim() || saving} loading={saving} onClick={save}>
            {saving
              ? (isEditing ? t('dataEntry.updating') : t('dataEntry.saving'))
              : (isEditing ? t('dataEntry.update') : t('dataEntry.save'))}
          </PillButton>
        </div>
      </Card>

      {/* PasswordGenerator ya monta su propio overlay (password-generator-overlay
          en su CSS): envolverlo además en el Modal del kit duplicaría el fondo. */}
      {generatorFor && (
        <PasswordGenerator
          onSelect={(pwd) => { setValue('password', pwd); setGeneratorFor(null); }}
          onClose={() => setGeneratorFor(null)}
        />
      )}
    </div>
  );
}

export default ItemForm;
